import type { JsonRecord } from "@/lib/fund-compliance/parse-primitives";

/* ── Heading-based section stripper ──────────────────────────────── */

const HEADING_RE = /^(#{1,6})\s+(.+?)\s*$/;
const FENCE_RE = /^```/;

/**
 * Remove markdown sections whose heading matches any of the given patterns.
 *
 * A section starts at its heading line and extends until the next heading of
 * equal or higher level (fewer `#`), or EOF. Fenced code blocks are
 * respected — `#` lines inside them are not treated as headings.
 *
 * Heading text is normalized (markdown emphasis `*` / `_` stripped, trimmed)
 * before being matched against the patterns.
 */
export function stripMarkdownSections(markdown: string, patterns: RegExp[]): string {
  if (!markdown || patterns.length === 0) return markdown;

  const lines = markdown.split("\n");
  const result: string[] = [];
  let skipUntilLevel: number | null = null;
  let inFence = false;

  for (const line of lines) {
    if (FENCE_RE.test(line)) {
      inFence = !inFence;
      if (skipUntilLevel === null) result.push(line);
      continue;
    }

    if (inFence) {
      if (skipUntilLevel === null) result.push(line);
      continue;
    }

    const headingMatch = line.match(HEADING_RE);
    if (headingMatch) {
      const hashes = headingMatch[1] ?? "";
      const rawTitle = headingMatch[2] ?? "";
      const level = hashes.length;
      const title = rawTitle.replace(/[*_`]/g, "").trim();

      // Leaving the dropped section when a same-or-higher-level heading appears
      if (skipUntilLevel !== null && level <= skipUntilLevel) {
        skipUntilLevel = null;
      }

      if (skipUntilLevel === null && patterns.some((p) => p.test(title))) {
        skipUntilLevel = level;
        continue;
      }

      if (skipUntilLevel !== null) continue;
      result.push(line);
      continue;
    }

    if (skipUntilLevel !== null) continue;
    result.push(line);
  }

  // Collapse the blank-line runs the removed sections leave behind, then trim edges
  return result.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

/* ── Review-specific pattern selection ───────────────────────────── */

const FINDINGS_HEADING_PATTERNS: RegExp[] = [
  /^(compliance\s+|key\s+|critical\s+)?findings?$/i,
  /^(compliance\s+|key\s+)?issues?$/i,
];

const ACTIONS_HEADING_PATTERNS: RegExp[] = [
  /^(recommended|suggested|next)\s+actions?$/i,
  /^recommendations?$/i,
];

const NOTES_HEADING_PATTERNS: RegExp[] = [
  /^source[\s-]*backed\s+notes?$/i,
];

/**
 * Given the structured review payload that will be rendered separately as
 * FindingsGrid / BulletsBlock / Notes text, compute which heading patterns
 * should be stripped from the narrative text block so the same content is
 * not shown twice.
 *
 * Only returns patterns for fields that are actually populated in the
 * review data — if there are no structured findings, the narrative
 * "Findings" heading is left alone.
 */
export function computeReviewStripPatterns(
  reviewData: JsonRecord | null | undefined
): RegExp[] {
  if (!reviewData) return [];
  const patterns: RegExp[] = [];

  const issues = reviewData.issues ?? reviewData.findings;
  if (Array.isArray(issues) && issues.length > 0) {
    patterns.push(...FINDINGS_HEADING_PATTERNS);
  }

  const actions =
    reviewData.recommendedActions ?? reviewData.actions ?? reviewData.recommendations;
  if (Array.isArray(actions) && actions.length > 0) {
    patterns.push(...ACTIONS_HEADING_PATTERNS);
  }

  const notes = reviewData.sourceBackedNotes ?? reviewData.notes;
  if (typeof notes === "string" && notes.trim().length > 0) {
    patterns.push(...NOTES_HEADING_PATTERNS);
  }

  return patterns;
}

/**
 * Strip narrative markdown sections that are already being rendered via
 * structured review blocks (findings grid, recommended-action bullets,
 * source-backed notes). Returns the possibly-shortened text — may be "" if
 * the narrative was entirely composed of duplicated sections.
 */
export function stripRedundantReviewSections(
  text: string,
  reviewData: JsonRecord | null | undefined
): string {
  const patterns = computeReviewStripPatterns(reviewData);
  if (patterns.length === 0) return text;
  return stripMarkdownSections(text, patterns);
}
